import {
  Building2,
  BookOpen,
  ChevronDown,
  Plus,
  Settings,
  LogOut,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import { checkOutletLimit } from "@/lib/limits";
import { isDemoMode } from "@/lib/demo/demoMode";
import { guardDemoAction } from "@/lib/demo/demoGuard";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";

interface OutletSwitcherProps {
  /** Navigate to a top-level admin section. */
  onSectionChange?: (section: string, extraParams?: Record<string, string>) => void;
}

export default function OutletSwitcher({ onSectionChange }: OutletSwitcherProps) {
  const { state: sidebarState } = useSidebar();
  const { businessId, isStaff, signOut } = useAuth();
  const { outlets, activeOutlet, isAllOutlets, loading, setActiveOutlet } = useOutlet();
  const navigate = useNavigate();

  const { data: businessData } = useQuery({
    queryKey: ["businessSidebar", businessId],
    queryFn: async () => {
      if (!businessId) return null;
      const { data } = await supabase
        .from("businesses")
        .select("slug, subscription_tier")
        .eq("business_id", businessId)
        .maybeSingle();
      return data;
    },
    enabled: !!businessId,
    staleTime: 30000,
  });

  const subscriptionTier = businessData?.subscription_tier || null;
  const menuItemClass =
    "focus:!bg-muted/60 focus:!text-foreground data-[highlighted]:!bg-muted/60 data-[highlighted]:!text-foreground";
  // Keep the badge anchored at the same left offset while the sidebar width
  // animates. Centering it as soon as collapse starts makes it jump to the
  // middle of the still-wide trigger before travelling back to the left.
  const triggerClass = "w-full h-full flex items-center gap-2 px-3 outline-none hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset";
  const contentClass = sidebarState === "collapsed"
    ? "w-56"
    : "w-[var(--radix-dropdown-menu-trigger-width)]";

  const staffKnowledgeNavigationItem = (
    <DropdownMenuItem
      className={menuItemClass}
      onClick={() => onSectionChange?.("knowledge")}
    >
      <BookOpen className="h-4 w-4 mr-2" />
      Knowledge
    </DropdownMenuItem>
  );

  // Dimmed but still clickable in the demo, so the click can explain itself.
  const demoBlocked = isDemoMode() ? "opacity-50 cursor-not-allowed" : "";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const activeOutlets = outlets.filter((outlet) => outlet.is_active !== false);
  const outletName = loading
    ? "Loading..."
    : isAllOutlets
      ? "All outlets"
      : activeOutlet?.outlet_name || activeOutlets[0]?.outlet_name || "No branches";
  const BranchBadge = ({ name, className = "" }: { name: string; className?: string }) => (
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary text-[11px] font-bold text-primary-foreground ${className}`}
      aria-hidden="true"
    >
      {(name || "?").charAt(0).toUpperCase()}
    </span>
  );
  const outletMark = isAllOutlets
    ? (
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary" aria-hidden="true">
        <Building2 className="h-4 w-4 text-primary-foreground" />
      </span>
    )
    : <BranchBadge name={outletName} />;

  if (isStaff && activeOutlets.length <= 1) {
    /* Staff (Single Outlet): workspace navigation without owner-only controls. */
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={triggerClass}>
            {outletMark}
            <span className="flex-1 min-w-0 truncate text-left font-bold text-sm group-data-[collapsible=icon]:hidden">{outletName}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className={contentClass}>
          {staffKnowledgeNavigationItem}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (isStaff) {
    /* Staff (Multiple Outlets): switcher without create option */
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={triggerClass}>
            {outletMark}
            <span className="flex-1 min-w-0 truncate text-left font-bold text-sm group-data-[collapsible=icon]:hidden">{outletName}</span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className={contentClass}>
          <DropdownMenuItem
            onClick={() => setActiveOutlet(null)}
            className={`${menuItemClass} ${isAllOutlets ? "bg-muted/60 font-medium" : ""}`}
          >
            <span className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary" aria-hidden="true">
              <Building2 className="h-4 w-4 text-primary-foreground" />
            </span>
            All outlets
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {activeOutlets.map((outlet) => (
            <DropdownMenuItem
              key={outlet.outlet_id}
              onClick={() => setActiveOutlet(outlet)}
              className={`${menuItemClass} ${activeOutlet?.outlet_id === outlet.outlet_id ? "bg-muted/60 font-medium" : ""}`}
            >
              <BranchBadge name={outlet.outlet_name} className="mr-2" />
              {outlet.outlet_name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {staffKnowledgeNavigationItem}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  /* Admin: full branch switcher + merged account menu */
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={triggerClass}>
          {outletMark}
          <span className="flex-1 min-w-0 truncate text-left font-bold text-sm group-data-[collapsible=icon]:hidden">{outletName}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={contentClass}>
        {activeOutlets.length > 1 && (
          <>
            <DropdownMenuItem
              onClick={() => setActiveOutlet(null)}
              className={`${menuItemClass} ${isAllOutlets ? "bg-muted/60 font-medium" : ""}`}
            >
              <span className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary" aria-hidden="true">
                <Building2 className="h-4 w-4 text-primary-foreground" />
              </span>
              All outlets
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {activeOutlets.map((outlet) => (
          <DropdownMenuItem
            key={outlet.outlet_id}
            onClick={() => setActiveOutlet(outlet)}
            className={`${menuItemClass} ${activeOutlet?.outlet_id === outlet.outlet_id ? "bg-muted/60 font-medium" : ""}`}
          >
            <BranchBadge name={outlet.outlet_name} className="mr-2" />
            {outlet.outlet_name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          className={`${menuItemClass} ${demoBlocked}`}
          disabled={!checkOutletLimit(subscriptionTier, outlets.length)}
          onClick={guardDemoAction(() => onSectionChange?.("settings", { settings: "branches" }))}
          title={!checkOutletLimit(subscriptionTier, outlets.length) ? "Upgrade plan to create more branches" : "Add a new branch"}
        >
          <Plus className="h-4 w-4 mr-2" />
          {!checkOutletLimit(subscriptionTier, outlets.length) ? "Upgrade plan to add branches" : "New branch"}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem className={`${menuItemClass} ${demoBlocked}`} onClick={guardDemoAction(() => onSectionChange?.("settings"))}>
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem className={`${menuItemClass} ${demoBlocked}`} onClick={guardDemoAction(handleSignOut)}>
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
